import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Button, Dropdown, Menu } from '@prio365/prio365-react-library';
import MenuItem from '@prio365/prio365-react-library/lib/Menu/components/MenuItem';
import { Modal, notification, Typography } from 'antd';
import classNames from 'classnames';
import { useCallback, useEffect, useRef, useState } from 'react';
import { DropTargetMonitor } from 'react-dnd';
import { useTranslation } from 'react-i18next';
import { useTheme } from 'react-jss';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import {
  getActiveProject,
  getCurrentFolderChildren,
  getCurrentFolderItem,
  getDocumentsWidgetActiveProjectId,
  getDriveItemIsFetching,
  getProject,
  getUserMe,
  RootReducerState,
} from '../../../../apps/main/rootReducer';
import { copyToClipboard } from '../../../../components/CopyableTextTile';
import DebouncedInputSearch from '../../../../components/DebouncedInputField/DebouncedInputSearch';
import Flex from '../../../../components/Flex';
import PrioSpinner from '../../../../components/PrioSpinner';
import UploadField, {
  PrioFile,
} from '../../../../components/Upload/UploadField';
import DroppableElement from '../../../../dnd/components/DroppableElement';
import {
  DND_TYPE_DRIVE_ITEM_FILE,
  DND_TYPE_EMAIL,
  DND_TYPE_EMAIL_ATTACHMENT,
  DND_TYPE_PROMAN_REPORT,
  DndDriveItemDto,
} from '../../../../dnd/types';
import { NewDocumentType } from '../../../../models/Document';
import { DriveItem, FolderDriveItem } from '../../../../models/Drive';
import { Project } from '../../../../models/Project';
import { DriveItemId, MessageId, ProjectId } from '../../../../models/Types';
import { PrioTheme } from '../../../../theme/types';
import { makePrioStyles } from '../../../../theme/utils';
import * as ConfigValues from '../../../../util/configValues';
import { isProjectAdmin } from '../../../projects/utils';
import { CreateFolderRequest } from '../../../settings/components/NewFolderForm';
import NewFolderModal from '../../../settings/components/NewFolderModal';
import { setActiveProjectDocumentsWidget } from '../../../widgetArea/actions';
import {
  abortDriveItemFetch,
  copyItemsIntoFolder,
  driveItemDeleted,
  driveItemNewFolderCreated,
  DriveItemsFetchContext,
  fetchDriveItemsSagaAction,
} from '../../actions';
import { fetchDriveFavorites } from '../../actions/driveFavorites';
import { updateDriveItemPath } from '../../actions/lastOpenDriveItemFolder';
import { setDocumentsMetaState } from '../../actions/meta';
import { addPrioFilesToUploadList } from '../../actions/uploadLists';
import {
  addDriveItemPermissions,
  apiCreateDriveFolder,
  apiDeleteDriveItem,
  apiDownloadDriveItem,
  apiZipDownload,
} from '../../api';
import { useDownloadXInvoice } from '../../hooks/useDownloadXInvoice';
import { useDriveItemActions } from '../../hooks/useDriveItemActions';
import {
  sagaStartUploadFiles,
  sagaUploadFiles,
} from '../../sagas/watchUploadFiles';
import {
  checkDriveItemNameConflicts,
  checkUploadConflicts,
  dispatchUploadFiles,
  handleDriveItemDrop,
  isDriveItemFolder,
  refreshDriveItems,
} from '../../util';
import CreateNewDriveItemDropdownOptions from '../CreateNewDriveItemDropdownOptions';
import { OnMailAttachmentsDrop } from '../DraggableDriveItem';
import { NewDocumentDrawer } from '../Drawers/NewDocumentDrawer';
import { NewDocumentFromTemplateDrawer } from '../Drawers/NewDocumentFromTemplateDrawer';
import { LegacyNewDocumentFromTemplateDrawer } from '../Drawers/LegacyNewDocumentFromTemplateDrawer';
import DriveItemBreadcrumb from '../DriveItemBreadcrumb/DriveItemBreadcrumb';
import FolderAccessRightsModal from '../FolderAccessRightsModal';
import { FileNameConflictBehaviour } from '../Modals/DriveItemConflictModal';
import RenameModal from '../Modals/RenameModal';
import DocumentsWidgetTable from './DocumentsWidgetTable';
import {
  MessageAttachment,
  MessageAttachmentWithId,
} from '../../../../models/Message';
import { checkFileNamesExists } from '../../util';
import { apiUploadAttachments } from '../../../mail/api';

const useStyles = makePrioStyles((theme: PrioTheme) => ({
  root: {
    height: '100%',
    width: '100%',
    '& .ant-upload': { height: '100%', width: '100%' },
    fontSize: theme.old.components.documentsTable.font.fontSize,
    lineHeight: theme.old.components.documentsTable.font.lineHeight,
  },
  content: {
    flex: 1,
    height: '100%',
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
  },
  flexBreadcrumb: { overflow: 'hidden' },
  searchInput: {
    width: '95%',
    margin: 10,
    '&.ant-input-search > .ant-input-group > .ant-input-group-addon:last-child':
      {
        border: theme.old.borders.content,
        '& .ant-input-search-button': { height: '100%' },
        '&:hover': { borderColor: 'var(--ant-primary-5)' },
      },
  },
  flexRow: {
    height: 32,
    alignItem: 'center',
    padding: theme.old.spacing.unit(0.5),
  },
  pointerOnHover: { '&:hover': { cursor: 'pointer' } },
  dropdown: { padding: 0, zIndex: 999 },
  menuItem: {
    margin: 0,
    fontSize: theme.font.fontSize.small,
    '& .prio-menu-suffixIcon': { display: 'flex' },
  },
  activeButton: {
    background: `${theme.colors.application.background.hover}!important`,
  },
}));

interface DocumentsWidgetProps {
  className?: string;
  projectId: ProjectId;
  initialDriveItemId?: DriveItemId;
  size?: number;
  onProjectIdChange?: (projectId: ProjectId) => void;
  onDriveItemIdChange?: (driveItemId: DriveItemId) => void;
  setNewDriveItemDestinationId?: (driveItemId: DriveItemId) => void;
}

export const DocumentsWidget = (props: DocumentsWidgetProps) => {
  //#region ------------------------------ Defaults
  const classes = useStyles();
  const {
    projectId,
    className,
    initialDriveItemId: currentFolderDriveItemId,
    onProjectIdChange,
    onDriveItemIdChange,
    setNewDriveItemDestinationId,
  } = props;
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const theme = useTheme<PrioTheme>();
  const location = useLocation();
  //#endregion

  //#region -------------------------------- Selectors

  //#endregion

  //#region -------------------------------- Refs
  const widgetBarIsDroppable = useRef<boolean>(false);
  //#endregion

  //#region -------------------------------- States
  const [openNewDocFromTemplateDrawer, setOpenNewDocFromTemplateDrawer] =
    useState<boolean>(false);
  const [openLegacyDocFromTemplateDrawer, setOpenLegacyDocFromTemplateDrawer] =
    useState<boolean>(false);
  const [openNewDocDrawer, setOpenNewDocDrawer] = useState<boolean>(false);
  const [documentType, setDocumentType] = useState<NewDocumentType>(null);
  const [creationPrefix, setCreationPrefix] = useState<string>(null);
  const [searchInput, setSearchInput] = useState<string>('');

  const [renameModalDriveItem, setRenameModalDriveItem] =
    useState<DriveItem>(null);

  const [isKeywordSearch, setIsKeywordSearch] = useState<boolean>(false);

  const [searchDriveItemId, setSearchDriveItemId] = useState<DriveItemId>(null);

  const [isDropdownContextMenuOpen, setIsDropdownContextMenuOpen] =
    useState<boolean>(false);

  const [isDropdownNewDocumentOpen, setIsDropdownNewDocumentOpen] =
    useState<boolean>(false);
  //#endregion

  const widgetAreaActiveProject = useSelector<RootReducerState, Project>(
    (state) => getProject(state, projectId)
  );

  const currentFolderChildren = useSelector<RootReducerState, DriveItem[]>(
    (state) =>
      getCurrentFolderChildren(
        state,
        currentFolderDriveItemId ?? rootDriveItemFolderId
      ) ?? []
  );

  const activeProjectId = useSelector(getActiveProject);

  const isRoot = !currentFolderDriveItemId;

  const groupId = widgetAreaActiveProject?.groupId;

  const [isReloadingDocuments, setIsReloadingDocuments] =
    useState<boolean>(false);

  const [isSearchingDocuments, setIsSearchingDocuments] =
    useState<boolean>(false);

  const rootDriveItemFolderId = `root-group-${groupId}`;

  const currentFolderDriveItem = useSelector<RootReducerState, DriveItem>(
    (state) =>
      getCurrentFolderItem(
        state,
        searchDriveItemId ?? currentFolderDriveItemId ?? rootDriveItemFolderId
      )
  );

  const [newFolderModalOpen, setNewFolderModalOpen] = useState<boolean>(false);

  const [newFolderCreating, setNewFolderCreating] = useState<boolean>(false);

  const { isFetching } = useSelector<
    RootReducerState,
    { isFetching: boolean; isFetchingWithNextLink: boolean }
  >((state) =>
    getDriveItemIsFetching(
      state,
      isRoot ? rootDriveItemFolderId : currentFolderDriveItemId
    )
  );

  const rootFolderDriveItemId = useSelector<RootReducerState, DriveItem>(
    (state) => getCurrentFolderItem(state, rootDriveItemFolderId)
  )?.id;

  const userMe = useSelector(getUserMe);

  const [isFolderAccessRightModalVisible, setIsFolderAccessRightModalVisible] =
    useState<boolean>(false);

  const [accessRightModalDriveItem, setAccessRightModalDriveItem] =
    useState<DriveItem>(null);
  const [isDocumentsModuleOpen, setIsDocumentsModuleOpen] =
    useState<boolean>(false);
  const [currentDriveItemInTable, setCurrentDriveItemInTable] =
    useState<DriveItemId>(undefined);

  const isRootAndProjectAdmin =
    isRoot && isProjectAdmin(widgetAreaActiveProject, userMe);

  const documentsWidgetActiveProjectId = useSelector(
    getDocumentsWidgetActiveProjectId
  );

  const { copyAsPdf } = useDriveItemActions(groupId, projectId);
  //#endregion

  //#region -------------------------------- Methods

  const onCreatedDocumentSuccess = (folder: DriveItem) => {
    setOpenNewDocFromTemplateDrawer(false);
    setOpenLegacyDocFromTemplateDrawer(false);
    setOpenNewDocDrawer(false);
    dispatch(updateDriveItemPath(projectId, { documentsWidget: folder.id }));
  };

  const createFolder = useCallback(async () => {
    if (!!groupId || isRootAndProjectAdmin) {
      setNewFolderModalOpen(true);
    }
    setIsDropdownContextMenuOpen(false);
  }, [groupId, isRootAndProjectAdmin]);

  const closeRenameModal = () => {
    setRenameModalDriveItem(null);
  };

  const closeNewFolderModal = () => {
    setNewFolderModalOpen(false);
  };

  const disableDrag = (item, monitor, targetDropItem, isOverCurrent) => {
    if (!widgetAreaActiveProject || isFetching) {
      return true;
    }
    const itemType = monitor.getItemType();

    switch (itemType) {
      case DND_TYPE_DRIVE_ITEM_FILE: {
        const draggedElement = monitor.getItem() as DndDriveItemDto;
        const draggedIsFolder = !!draggedElement?.driveItem?.folder;
        const targetIsRoot = !targetDropItem?.driveItem?.parentReference?.path;

        if (draggedIsFolder && targetIsRoot) return true;
        if (
          !targetDropItem?.driveItem?.id ||
          (draggedElement.driveItems ?? [draggedElement.driveItem]).some(
            (x) => x.id === targetDropItem.driveItem.id
          ) ||
          monitor.isOver({ shallow: true }) === false
        ) {
          return true;
        }
        return false;
      }
      case DND_TYPE_EMAIL: {
        return false;
      }
      case DND_TYPE_EMAIL_ATTACHMENT: {
        return false;
      }
    }
    return true;
  };

  const onSearchInputChange = (value: string) => {
    setSearchInput(value);
  };

  //#endregion

  //#region ------------------------------ Handlers
  const handleReloadDocuments = async () => {
    setIsReloadingDocuments(true);
    setIsDropdownContextMenuOpen(false);
    if (groupId) {
      dispatch(
        fetchDriveItemsSagaAction(
          projectId,
          groupId,
          currentFolderDriveItemId,
          isRoot,
          DriveItemsFetchContext.WidgetArea
        )
      );
      setTimeout(() => {
        setIsReloadingDocuments(false);
      }, 5000);
    }
  };

  const onChangeFolderAccessRights = (driveItem: DriveItem) => {
    setIsFolderAccessRightModalVisible(true);
    setAccessRightModalDriveItem(driveItem);
  };

  const handleJumpToTableDestination = () => {
    setIsDropdownContextMenuOpen(false);
    if (onProjectIdChange) {
      onProjectIdChange(activeProjectId);
    }
    if (setNewDriveItemDestinationId) {
      setNewDriveItemDestinationId(
        currentDriveItemInTable
          ? currentDriveItemInTable
          : rootDriveItemFolderId
      );
    }
  };

  const handleSearchingDocuments = async () => {
    setIsSearchingDocuments(true);
    setIsDropdownContextMenuOpen(false);
  };

  const onDriveItemClick = (driveItem: DriveItem) => {
    setNewDriveItemDestinationId(undefined);
    const isFolder = isDriveItemFolder(driveItem);

    if (isFolder) {
      dispatch(
        updateDriveItemPath(projectId, { documentsWidget: driveItem.id })
      );
    }
  };

  const onMailAttachmentsDrop: OnMailAttachmentsDrop = async (
    attachment,
    targetDriveItem,
    groupId,
    projectId,
    selectedAttachments,
    isDroppedOnFolder = false
  ) => {
    if (
      !attachment &&
      (!selectedAttachments || selectedAttachments.length === 0)
    ) {
      return;
    }

    const fallbackMessageId =
      (selectedAttachments &&
        selectedAttachments.find((a: any) => a.messageId)?.messageId) ||
      (attachment as any)?.messageId ||
      (attachment as any)?.attachment?.messageId ||
      '';

    const baseAttachments =
      Array.isArray(selectedAttachments) && selectedAttachments.length > 0
        ? selectedAttachments
        : [attachment];

    const attachments: MessageAttachment[] = baseAttachments
      .map((att) => {
        let baseAttachment: MessageAttachment | null = null;
        let messageId: MessageId | undefined;

        if ('attachment' in att && att.attachment && 'messageId' in att) {
          baseAttachment = att.attachment;
          messageId = att.messageId;
        } else if (
          att &&
          typeof att === 'object' &&
          'id' in att &&
          'contentType' in att &&
          'contentId' in att &&
          'isInline' in att
        ) {
          baseAttachment = att as MessageAttachment;
          messageId = (att as any).messageId;
        }

        if (!baseAttachment) return null;

        return {
          ...baseAttachment,
          messageId: messageId ?? fallbackMessageId,
        };
      })
      .filter((att): att is MessageAttachmentWithId => att !== null);

    if (attachments.length === 0) {
      return;
    }

    const names = Array.from(
      new Set(
        attachments
          .map((att) => att.name)
          .filter(
            (name) => !!name && typeof name === 'string' && name.trim() !== ''
          )
      )
    );
    if (names.length === 0) {
      return;
    }
    const checkedNames = await checkFileNamesExists(
      groupId,
      targetDriveItem.id,
      names
    );
    const folderResponse = checkedNames.find(
      (folder) => folder.parentFolderId === targetDriveItem.id
    );
    const hasConflicts =
      folderResponse &&
      folderResponse.fileNames.some((checked) => checked.exists);

    // Duplikat vorhanden
    if (hasConflicts) {
      dispatch(
        setDocumentsMetaState({
          driveItemConflictModalState: {
            behavior: 'copy',
            context: DriveItemsFetchContext.WidgetArea,
            modelType: 'messageAttachment',
            mailId: attachment.messageId,
            destinationFolderId: targetDriveItem.id,
            destinationProjectId: documentsWidgetActiveProjectId,
            sourceProjectId: projectId,
            selectedAttachments: attachments,
            existingCheckedNames: checkedNames,
          },
          showDriveItemConflictModalState: 'widget',
        })
      );
      return;
    }

    // SMIME und Kein Duplikat
    const allHaveContentBytes = attachments.every((att) => !!att.contentBytes);
    if (allHaveContentBytes) {
      for (const att of attachments) {
        const fileDataUrl = `data:${att.contentType};base64,${att.contentBytes}`;
        const file = await import('../../../../util').then((mod) =>
          mod.urltoFile(fileDataUrl, att.name, att.contentType)
        );
        const { result } = await apiUploadAttachments(
          file,
          documentsWidgetActiveProjectId,
          groupId,
          targetDriveItem.id
        );
        if (!(result.status >= 200 && result.status < 300)) {
          notification.open({
            message: t('common:error'),
            description: t('documents:errorMessages.saveAttachmentsError'),
          });
        }
      }
      refreshDriveItems(
        targetDriveItem,
        dispatch,
        documentsWidgetActiveProjectId,
        groupId,
        isDroppedOnFolder
      );
      return;
    }

    // Kein Duplikat
    const data = {
      targetDriveItemId: targetDriveItem.id,
      groupId: groupId,
      attachments: attachments.map((att) => ({
        messageId: att.messageId,
        attachmentId: att.id,
        fileName: att.name,
        hasContentBytes: !!att.contentBytes,
      })),
      conflictBehaviour: FileNameConflictBehaviour.RENAME,
    };

    try {
      const { result } = await import('../../../mail/api').then((mod) =>
        mod.apiSaveAttachmentsToCloudWithChunking(projectId, data)
      );
      if (result.status >= 200 && result.status < 300) {
        refreshDriveItems(
          targetDriveItem,
          dispatch,
          projectId,
          groupId,
          isDroppedOnFolder
        );
      } else {
        notification.open({
          message: t('common:error'),
          description: t('documents:errorMessages.saveAttachmentsError'),
        });
      }
    } catch {
      notification.open({
        message: t('common:error'),
        description: t('documents:errorMessages.saveAttachmentsError'),
      });
    }
  };

  const onMenuRename = (driveItem: DriveItem) => {
    setRenameModalDriveItem(driveItem);
  };

  const onMenuMove = (driveItem: DriveItem) => {
    dispatch(
      setDocumentsMetaState({
        driveItemConflictModalState: {
          behavior: 'move',
          context: DriveItemsFetchContext.WidgetArea,
          modelType: 'driveItem',
          selectedDriveItems: [driveItem],
          destinationFolderId: currentFolderDriveItemId,
          destinationProjectId: projectId,
          sourceFolderId: currentFolderDriveItemId,
          sourceProjectId: projectId,
        },
        showDriveItemConflictModalState: 'widget',
      })
    );
  };

  const onMenuCopy = (driveItem: DriveItem) => {
    dispatch(
      setDocumentsMetaState({
        driveItemConflictModalState: {
          behavior: 'copy',
          context: DriveItemsFetchContext.WidgetArea,
          modelType: 'driveItem',
          selectedDriveItems: [driveItem],
          destinationFolderId: currentFolderDriveItemId,
          destinationProjectId: projectId,
          sourceFolderId: currentFolderDriveItemId,
          sourceProjectId: projectId,
        },
        showDriveItemConflictModalState: 'widget',
      })
    );
  };

  const onMenuDownload = async (driveItem: DriveItem) => {
    if (isDriveItemFolder(driveItem)) {
      const zipDownloadKey = 'zip-download-preparing';
      notification.open({
        key: zipDownloadKey,
        message: t('documents:errorMessages.downloadZipPreparing'),
        icon: <PrioSpinner size="small" />,
        duration: 0,
      });
      const { result } = await apiZipDownload(groupId, [driveItem.id]);
      notification.close(zipDownloadKey);
      if (!result.ok) {
        notification.open({
          message: t('common:error'),
          description: t('documents:errorMessages.downloadFileError', {
            name: driveItem.name,
            count: 1,
          }),
        });
      }
    } else if (!(await apiDownloadDriveItem(groupId, driveItem))) {
      notification.open({
        message: t('common:error'),
        description: t('documents:errorMessages.downloadFileError', {
          name: driveItem.name,
          count: 1,
        }),
      });
    }
  };

  const onOpenParentFolder = (driveItem: DriveItem) => {
    setSearchDriveItemId(null);
    setSearchInput('');
    setIsSearchingDocuments(false);

    if (driveItem?.parentReference?.id !== currentFolderDriveItemId) {
      if (driveItem?.parentReference?.id === rootFolderDriveItemId) {
        dispatch(updateDriveItemPath(projectId, { documentsWidget: null }));
      } else {
        dispatch(
          updateDriveItemPath(projectId, {
            documentsWidget: driveItem.parentReference.id,
          })
        );
      }
    }
  };

  const onMenuDelete = (driveItem: DriveItem) => {
    Modal.confirm({
      icon: null,
      title: t('documents:confirmation.delete.title'),
      content: (
        <Flex.Column>
          <Typography.Text>
            {t('documents:confirmation.delete.content')}
          </Typography.Text>
          <ul>
            <li>
              <Typography.Text>{driveItem.name}</Typography.Text>
            </li>
          </ul>
        </Flex.Column>
      ),
      okText: t('documents:confirmation.delete.okText'),
      cancelText: t('documents:confirmation.delete.cancelText'),
      onOk() {
        const promise = new Promise<void>(async (resolve) => {
          const { result } = await apiDeleteDriveItem(groupId, driveItem.id);
          if (result.status >= 200 && result.status < 300) {
            dispatch(
              driveItemDeleted(driveItem.id, currentFolderDriveItemId, groupId)
            );
          } else {
            notification.open({
              message: t('common:error'),
              description: t('documents:errorMessages.deleteDocumentError'),
            });
          }

          resolve();
        });
        return promise;
      },
      onCancel() {},
    });
  };

  const onDndMove = (driveItem: DriveItem) => {};

  const onDndCopy = (driveItem: DriveItem) => {};

  const onNewFolderOk = async (value: CreateFolderRequest) => {
    if (value.folderName === '') {
      closeNewFolderModal();
      return;
    }
    setNewFolderCreating(true);
    const { data } = await apiCreateDriveFolder(
      groupId,
      currentFolderDriveItem?.id,
      value.folderName
    );
    if (isRoot) {
      await addDriveItemPermissions(data.id, value, projectId, groupId);
    }

    setNewFolderCreating(false);
    if (data) {
      dispatch(
        driveItemNewFolderCreated(
          data,
          currentFolderDriveItem?.id ?? null,
          groupId
        )
      );
    } else {
      notification.open({
        message: t('common:error'),
        description: t('documents:errorMessages.newFolderCreateError'),
      });
    }
    closeNewFolderModal();
  };

  const onNewFolderCancel = () => {
    closeNewFolderModal();
  };

  const onDriveItemBreadCrumbClick = (driveItemId: any) => {
    dispatch(updateDriveItemPath(projectId, { documentsWidget: driveItemId }));
    setIsSearchingDocuments(false);
    setSearchInput(null);
    setNewDriveItemDestinationId(undefined);
  };

  const onRowOver = (isOver: boolean) => {
    widgetBarIsDroppable.current = !isOver;
  };

  const onCopyAsPdf = async (driveItem: DriveItem) => {
    const success = await copyAsPdf(driveItem, projectId);
    if (success) {
      handleReloadDocuments();
    }
  };

  const onDropIntoDocumentsWidget = async (
    item: any,
    monitor: DropTargetMonitor<unknown, unknown>,
    target: FolderDriveItem,
    _dropResult: 'copy' | 'move'
  ) => {
    if (monitor.didDrop()) {
      return undefined;
    }
    let effectiveTarget = target;
    if (!target?.driveItem?.id && currentFolderDriveItem) {
      effectiveTarget = {
        ...target,
        driveItem: currentFolderDriveItem,
      };
    }

    const dropResult = await handleDriveItemDrop({
      dropResult: _dropResult,
      item,
      monitor,
      target: effectiveTarget,
      dispatch,
      checkDriveItemNameConflicts,
      setDocumentsMetaState,
      copyItemsIntoFolder,
      context: DriveItemsFetchContext.WidgetArea,
      FileNameConflictBehaviour,
    });

    const attachments = dropResult?.attachments;

    if (
      ((monitor.getItemType &&
        monitor.getItemType() === DND_TYPE_EMAIL_ATTACHMENT) ||
        (item && item.draggableMessageAttachment)) &&
      dropResult &&
      dropResult.type === 'attachment' &&
      typeof onMailAttachmentsDrop === 'function'
    ) {
      const targetDriveItem = effectiveTarget?.driveItem ?? null;

      const allAttachments =
        Array.isArray(attachments) && attachments.length > 0
          ? attachments
          : item.draggableMessageAttachment
            ? [item.draggableMessageAttachment]
            : [];

      if (allAttachments.length > 0) {
        await onMailAttachmentsDrop(
          allAttachments[0],
          targetDriveItem,
          effectiveTarget.groupId,
          item.projectId,
          allAttachments
        );
      }
    }

    return dropResult;
  };

  const handleOnBeforeUpload = async (
    originFiles: PrioFile[],
    sessionId: string
  ) => {
    const { hasFolderConflict, hasFileConflict } = checkUploadConflicts(
      originFiles,
      currentFolderChildren
    );
    if (hasFolderConflict || hasFileConflict) {
      dispatch(
        setDocumentsMetaState({
          driveItemConflictModalState: {
            behavior: 'upload',
            context: DriveItemsFetchContext.WidgetArea,
            modelType: 'file',
            destinationProjectId: projectId,
            selectedDriveItems: [],
            destinationFolderId: currentFolderDriveItemId,
            sessionId,
            fileList: originFiles,
          },
          showDriveItemConflictModalState: 'widget',
        })
      );
    } else {
      dispatch(
        sagaStartUploadFiles(
          groupId,
          sessionId,
          currentFolderDriveItemId ?? rootDriveItemFolderId,
          projectId
        )
      );
    }
  };

  const handleOnAfterUpload = async (
    fileList: PrioFile[],
    sessionId: string
  ) => {
    dispatchUploadFiles({
      dispatch,
      groupId,
      sessionId,
      currentFolderDriveItemId,
      rootDriveItemFolderId,
      projectId,
      fileList,
      addPrioFilesToUploadList,
      sagaUploadFiles,
      conflictBehaviour: 'rename',
    });
  };

  const { onDownloadXInvoice } = useDownloadXInvoice(groupId);
  //#endregion

  //#region ------------------------------ Effects
  useEffect(() => {
    if (projectId !== 'me') {
      dispatch(fetchDriveFavorites(projectId));
    }
    return () => {};
  }, [dispatch, projectId]);

  useEffect(() => {
    dispatch(setActiveProjectDocumentsWidget(projectId));
  }, [projectId, dispatch]);

  useEffect(() => {
    if (onDriveItemIdChange) {
      if (searchDriveItemId !== null) {
        onDriveItemIdChange(searchDriveItemId);
        dispatch(
          updateDriveItemPath(projectId, { documentsWidget: searchDriveItemId })
        );

        setIsSearchingDocuments(false);
        setSearchInput(null);
      } else {
        onDriveItemIdChange(currentFolderDriveItemId);
      }
    }
  }, [
    currentFolderDriveItemId,
    searchDriveItemId,
    onDriveItemIdChange,
    dispatch,
    projectId,
  ]);

  useEffect(() => {
    setIsDocumentsModuleOpen(location.pathname.includes('documents'));
    setCurrentDriveItemInTable(location.pathname.split('/folder/')[1]);
  }, [location]);

  useEffect(() => {
    return () => {
      dispatch(abortDriveItemFetch(DriveItemsFetchContext.WidgetArea));
    };
  }, [dispatch]);

  //#endregion

  return (
    <>
      <UploadField
        className={classNames(classes.root, className)}
        onBeforeUpload={handleOnBeforeUpload}
        onAfterUpload={handleOnAfterUpload}
        multiple
        directory
      >
        <DroppableElement
          activeOverlay
          accept={[
            DND_TYPE_DRIVE_ITEM_FILE,
            DND_TYPE_EMAIL,
            DND_TYPE_EMAIL_ATTACHMENT,
            DND_TYPE_PROMAN_REPORT,
          ]}
          onDrop={onDropIntoDocumentsWidget}
          object={
            {
              projectId: widgetAreaActiveProject?.projectId,
              groupId: groupId,
              driveItem: currentFolderDriveItem,
            } as FolderDriveItem
          }
          disable={disableDrag}
        >
          <Flex.Column className={classNames(classes.root, className)}>
            <div className={classes.content}>
              {!isSearchingDocuments ? (
                <Flex.Row alignItems="baseline" className={classes.flexRow}>
                  <Flex.Item flex={1} className={classes.flexBreadcrumb}>
                    <DriveItemBreadcrumb
                      driveItem={currentFolderDriveItem}
                      project={widgetAreaActiveProject}
                      onItemClick={onDriveItemBreadCrumbClick}
                      onRootChange={(projectId) => {
                        if (onProjectIdChange) {
                          onProjectIdChange(projectId);
                        }
                      }}
                      directionLeft
                      disableLinkNavigation
                      addProjectList
                    />
                  </Flex.Item>
                  {projectId && groupId && (
                    <Flex.Item>
                      <Dropdown
                        overlayClassName={classes.dropdown}
                        onVisibleChange={(visible) =>
                          setIsDropdownNewDocumentOpen(visible)
                        }
                        overlay={
                          <CreateNewDriveItemDropdownOptions
                            projectId={projectId}
                            setOpenNewDocFromTamplateDrawer={
                              setOpenNewDocFromTemplateDrawer
                            }
                            setOpenLegacyDocFromTemplateDrawer={
                              setOpenLegacyDocFromTemplateDrawer
                            }
                            setOpenNewDocDrawer={setOpenNewDocDrawer}
                            setIsDropdownNewDocumentOpen={
                              setIsDropdownNewDocumentOpen
                            }
                            onDocumentTypeSelect={(id, prefix) => {
                              setDocumentType(id);
                              setCreationPrefix(prefix);
                            }}
                            isRoot={isRoot}
                            onNewFolderClick={createFolder}
                            disableFolderMenuItem={
                              currentFolderDriveItem?.name === 'root' &&
                              !isRootAndProjectAdmin
                            }
                          />
                        }
                      >
                        <Button
                          style={{ marginRight: 4 }}
                          className={classNames({
                            [classes.activeButton]: isDropdownNewDocumentOpen,
                          })}
                          type="default"
                          iconProp={['fal', 'plus']}
                          disabled={
                            currentFolderDriveItem?.name === 'root' &&
                            !isRootAndProjectAdmin
                          }
                        ></Button>
                      </Dropdown>

                      <Dropdown
                        trigger={['click']}
                        overlayClassName={classes.dropdown}
                        onVisibleChange={(visible) =>
                          setIsDropdownContextMenuOpen(visible)
                        }
                        overlay={
                          <Menu className={classes.dropdown} selectedItemId="">
                            <MenuItem
                              id="search"
                              className={classes.menuItem}
                              onClick={handleSearchingDocuments}
                              disabled={isSearchingDocuments}
                              icon={['fal', 'search']}
                              label={t(
                                'documents:widget.header.actions.search'
                              )}
                            />
                            <MenuItem
                              id="reload"
                              className={classes.menuItem}
                              onClick={handleReloadDocuments}
                              disabled={isReloadingDocuments}
                              icon={['fal', 'redo']}
                              label={t(
                                'documents:widget.header.actions.reload'
                              )}
                            />
                            <MenuItem
                              id="jumpToTableDestination"
                              className={classes.menuItem}
                              onClick={handleJumpToTableDestination}
                              icon={['fal', 'arrow-up-right-from-square']}
                              label={t(
                                'documents:widget.header.actions.jumpToTableDestination'
                              )}
                              disabled={!isDocumentsModuleOpen}
                            />
                            {/* <MenuItem
                              id="createFolder"
                              className={classes.menuItem}
                              disabled={
                                currentFolderDriveItem?.name === 'root' &&
                                !isRootAndProjectAdmin
                              }
                              onClick={createFolder}
                              iconProp={['fal', 'folder-plus']}
                              label={t(
                                'documents:widget.header.actions.createFolder'
                              )}
                            /> */}
                            <MenuItem
                              id="copyPrioLink"
                              className={classes.menuItem}
                              icon={['fal', 'link']}
                              onClick={() => {
                                copyToClipboard(
                                  `${
                                    ConfigValues.REACT_APP_API_REDIRECT_URL
                                  }module/prio/projects/${projectId}/documents/${
                                    currentFolderDriveItem?.name === 'root'
                                      ? 'all'
                                      : `folder/${currentFolderDriveItem?.id}`
                                  }`
                                );
                                setIsDropdownContextMenuOpen(false);
                              }}
                              label={t(
                                'documents:widget.header.actions.copyPrioUrl'
                              )}
                            />
                            <MenuItem
                              id="copySharepointLink"
                              className={classes.menuItem}
                              icon={['fal', 'link']}
                              onClick={() => {
                                copyToClipboard(
                                  currentFolderDriveItem?.webDavUrl
                                );
                                setIsDropdownContextMenuOpen(false);
                              }}
                              label={t(
                                'documents:widget.header.actions.copySharepointUrl'
                              )}
                            />
                          </Menu>
                        }
                        placement="bottomRight"
                      >
                        <Button
                          type="default"
                          iconProp={['fal', 'ellipsis-v']}
                          className={classNames({
                            [classes.activeButton]: isDropdownContextMenuOpen,
                          })}
                        />
                      </Dropdown>
                    </Flex.Item>
                  )}
                </Flex.Row>
              ) : (
                <>
                  <DebouncedInputSearch
                    size="small"
                    placeholder={t(
                      'documents:search.placeholderWithProjectNumber',
                      { projectNumber: widgetAreaActiveProject.number }
                    )}
                    onChange={onSearchInputChange}
                    className={classes.searchInput}
                    value={searchInput}
                    autoFocus={true}
                    prefix={null}
                    addonAfter={
                      !isReloadingDocuments ? (
                        <FontAwesomeIcon
                          icon={['fal', 'redo']}
                          onClick={handleReloadDocuments}
                          className={classes.pointerOnHover}
                        />
                      ) : (
                        <PrioSpinner size="small" alignSelf></PrioSpinner>
                      )
                    }
                    onKeyDown={(e) => {
                      if (e.key === 'Escape') {
                        setIsSearchingDocuments(false);
                        setSearchInput(null);
                      }
                    }}
                    suffix={
                      <FontAwesomeIcon
                        icon={['fas', 'times-circle']}
                        onClick={() => {
                          setSearchInput(null);
                          setIsSearchingDocuments(false);
                        }}
                        color={theme.old.typography.colors.muted}
                      />
                    }
                  />
                  {!isKeywordSearch && searchInput !== '' && (
                    <Flex.Row alignItems="baseline" className={classes.flexRow}>
                      <Flex.Item flex={1} className={classes.flexBreadcrumb}>
                        <DriveItemBreadcrumb
                          driveItem={currentFolderDriveItem}
                          project={widgetAreaActiveProject}
                          onItemClick={onDriveItemBreadCrumbClick}
                          onRootChange={(projectId) => {
                            if (onProjectIdChange) {
                              onProjectIdChange(projectId);
                            }
                          }}
                          disableLinkNavigation
                          addProjectList
                        />
                      </Flex.Item>
                    </Flex.Row>
                  )}
                </>
              )}
              {projectId && groupId && (
                <>
                  <DocumentsWidgetTable
                    activeProjectId={widgetAreaActiveProject.projectId}
                    groupId={groupId}
                    currentFolderDriveItemId={currentFolderDriveItemId}
                    onMenuDelete={onMenuDelete}
                    onMenuDownload={onMenuDownload}
                    onMenuRename={onMenuRename}
                    onMenuMove={onMenuMove}
                    onMenuCopy={onMenuCopy}
                    onDndCopy={onDndCopy}
                    onDndMove={onDndMove}
                    onRowOver={onRowOver}
                    onCopyAsPdf={onCopyAsPdf}
                    onDriveItemClick={onDriveItemClick}
                    onMailAttachmentsDrop={onMailAttachmentsDrop}
                    onOpenParentFolder={onOpenParentFolder}
                    searchInput={searchInput}
                    setSearchInput={setSearchInput}
                    setIsKeywordSearch={setIsKeywordSearch}
                    isKeywordSearch={isKeywordSearch}
                    isSearchingDocuments={isSearchingDocuments}
                    setSearchDriveItemId={setSearchDriveItemId}
                    searchDriveItemId={searchDriveItemId}
                    setIsSearchingDocuments={setIsSearchingDocuments}
                    setProjectIdFromUrl={(projectId) => {
                      if (onProjectIdChange) {
                        onProjectIdChange(projectId);
                      }
                    }}
                    onEditFolderAccessRights={onChangeFolderAccessRights}
                    onDownloadXInvoice={onDownloadXInvoice}
                  />
                </>
              )}
            </div>
          </Flex.Column>
        </DroppableElement>
      </UploadField>
      {projectId && groupId && (
        <>
          <RenameModal
            renameDriveItem={renameModalDriveItem}
            folderDriveItemId={currentFolderDriveItemId}
            groupId={groupId}
            onClose={closeRenameModal}
          />
          <NewFolderModal
            currentPath={[
              currentFolderDriveItem?.name === 'root'
                ? t('documents:rootFolder')
                : (currentFolderDriveItem?.name ?? ''),
            ]}
            visible={newFolderModalOpen}
            onOk={onNewFolderOk}
            onCancel={onNewFolderCancel}
            confirmLoading={newFolderCreating}
            usePermissions={isRootAndProjectAdmin}
            groupId={groupId}
            parentReferenceId={
              currentFolderDriveItemId ?? rootFolderDriveItemId
            }
          />
        </>
      )}
      <NewDocumentFromTemplateDrawer
        visible={openNewDocFromTemplateDrawer}
        projectId={projectId}
        groupId={groupId}
        onCancel={() => setOpenNewDocFromTemplateDrawer(false)}
        onSuccess={onCreatedDocumentSuccess}
        driveItem={currentFolderDriveItem}
      />
      <LegacyNewDocumentFromTemplateDrawer
        visible={openLegacyDocFromTemplateDrawer}
        projectId={projectId}
        groupId={groupId}
        onCancel={() => setOpenLegacyDocFromTemplateDrawer(false)}
        onSuccess={onCreatedDocumentSuccess}
        driveItem={currentFolderDriveItem}
      />
      <NewDocumentDrawer
        visible={openNewDocDrawer}
        projectId={projectId}
        groupId={groupId}
        onCancel={() => setOpenNewDocDrawer(false)}
        onSuccess={onCreatedDocumentSuccess}
        driveItem={currentFolderDriveItem}
        parentFolderDriveItem={currentFolderDriveItem}
        documentType={documentType}
        prefix={creationPrefix}
      />
      <FolderAccessRightsModal
        projectId={projectId}
        groupId={groupId}
        driveItem={accessRightModalDriveItem}
        visible={isFolderAccessRightModalVisible}
        setVisible={setIsFolderAccessRightModalVisible}
      />
    </>
  );
};
export default DocumentsWidget;
